import { h } from "preact";
import { useState, useCallback, useRef, useMemo, useEffect } from "preact/hooks";
import type { WidgetConfig, WidgetProps, WidgetState, ChatMessage, SessionUsage, StudentIdentity } from "./types";
import { WidgetClient } from "./api/client";
import { capturePageText, hostPage, sha256Hex } from "./api/pageCapture";
import { useChat } from "./hooks/useChat";
import { ChatPanel } from "./components/ChatPanel";
import { configureMathLoader } from "./api/mathLoader";

/** Error codes that indicate the session is permanently exhausted */
const SESSION_FATAL_CODES = ["SESSION_LIMIT_EXCEEDED", "CONVERSATION_LIMIT_EXCEEDED"];

/** SVG chat bubble icon for FAB */
function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
    </svg>
  );
}

// ─── Session Persistence ────────────────────────────────────────
interface SavedSession {
  token: string;
  /** Absent until the student sends a first message (conversations are created lazily). */
  conversationId?: string;
  config: WidgetConfig;
  savedAt: number; // epoch ms
  totalUserMessages: number; // accumulated across all conversations in this session
  pageContextId?: string; // self-reading widgets: page context of the saved conversation
  /**
   * The page the saved conversation belongs to. Sessions are per widget key, and one key
   * serves many pages (the notes bot is on every topic page), so without this a
   * page-reading bot carried one page's conversation and capture onto the next page.
   */
  pageUrl?: string;
  /**
   * Was this conversation created under a UW sign-in?
   *
   * Only the guest token is persisted — a token carrying the student's UW identity is
   * not something to leave in a course page's localStorage — so a reload always comes
   * back signed out. Restoring an attributed conversation into a widget that reads
   * "Anonymous" would be the worst version of this feature: the student would keep
   * talking, believing they were anonymous, into a row that names them. Flagged here
   * so the restore path starts a new conversation instead.
   */
  uwSignedIn?: boolean;
}

/**
 * Where the student's self-reported identity lives.
 *
 * Deliberately NOT scoped to the widget key or session: a course site runs more than
 * one bot (notes vs. assignments = different keys, different chat sessions), and a
 * student who identified on a notes page should still be identified when they open an
 * assignment page. One identity per site, per browser, surviving session expiry — so
 * re-identifying is never more than the one click the toggle already costs.
 */
const IDENTITY_STORAGE_KEY = "css_tutor_widget:identity";

// Mirror of the server's normalizeIdentityField (conversation.service.ts) so the
// restore-time comparison below doesn't see a spurious diff and PATCH on every open.
const MAX_STUDENT_NAME_LENGTH = 120;
const MAX_STUDENT_NUMBER_LENGTH = 40;

function normalizeIdentityField(value: string | null | undefined, max: number): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
}

/** Build a localStorage key scoped to this widget's context */
function storageKey(opts: {
  widgetKey?: string;
  courseId?: string;
  sectionId?: string;
  assignmentId?: string;
}): string {
  // Keyed widgets: scope to the key (unique, survives course/section renames).
  if (opts.widgetKey) return `css_tutor_widget:key:${opts.widgetKey}`;
  // Keyless fallback: scope by course/section/assignment.
  const parts = ["css_tutor_widget", opts.courseId ?? ""];
  if (opts.sectionId) parts.push(opts.sectionId);
  if (opts.assignmentId) parts.push(opts.assignmentId);
  return parts.join(":");
}

function saveSession(key: string, session: SavedSession): void {
  try {
    localStorage.setItem(key, JSON.stringify(session));
  } catch { /* storage full or unavailable — ignore */ }
}

function loadSession(key: string): SavedSession | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const session: SavedSession = JSON.parse(raw);
    // JWT expires in 4h — discard if older than 3.5h to avoid mid-chat expiry
    const maxAge = 3.5 * 60 * 60 * 1000;
    if (Date.now() - session.savedAt > maxAge) {
      localStorage.removeItem(key);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function clearSession(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

// ─── App Component ──────────────────────────────────────────────
export function App(props: WidgetProps) {
  const { apiBaseUrl, courseId, sectionId, assignmentId, widgetKey, theme, position, maxMessages, logoUrl, contentSelector, pageContext } = props;
  // During render, not in an effect: child effects (MessageBubble's math pass) run
  // before the parent's, and must already know where widget-math.js lives.
  configureMathLoader(apiBaseUrl);

  const [widgetState, setWidgetState] = useState<WidgetState>("idle");
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [restoredMessages, setRestoredMessages] = useState<ChatMessage[]>([]);
  const [totalUserMessages, setTotalUserMessages] = useState(0);
  const [serverUsage, setServerUsage] = useState<SessionUsage | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [sessionExhausted, setSessionExhausted] = useState(false);
  // The student's model choice; null follows the section default.
  const [chatModel, setChatModel] = useState<string | null>(null);

  const clientRef = useRef<WidgetClient>(new WidgetClient(apiBaseUrl, widgetKey));
  const pageContextIdRef = useRef<string | undefined>(undefined);

  /**
   * Self-reading widgets: capture the host page, hash it, and resolve a
   * pageContextId via the two-phase cache protocol. Non-fatal — a capture
   * failure just means the bot runs without page context.
   */
  const resolvePageContext = useCallback(async (): Promise<string | undefined> => {
    if (pageContext === "off") return undefined;
    try {
      const capture = capturePageText(contentSelector);
      if (!capture) return undefined;
      const client = clientRef.current;
      const hash = await sha256Hex(capture.text);
      const cachedId = await client.lookupPageContext(capture.url, hash);
      if (cachedId) return cachedId;
      return await client.registerPageContext(capture.url, capture.text);
    } catch (err) {
      console.warn("[UWT Tutor Widget] Page capture failed — continuing without page context:", err);
      return undefined;
    }
  }, [pageContext, contentSelector]);
  const sessionKey = useMemo(
    () => storageKey({ widgetKey, courseId, sectionId, assignmentId }),
    [widgetKey, courseId, sectionId, assignmentId]
  );
  // ── Student self-identification (optional, for extra-credit attribution) ──
  // identityRecord = the name/number the student entered (remembered so re-identifying
  // is one click); identityActive = whether it's currently attached to conversations.
  const [identityRecord, setIdentityRecord] = useState<StudentIdentity | null>(null);
  const [identityActive, setIdentityActive] = useState(false);
  const [showIdentityGate, setShowIdentityGate] = useState(false);
  // Ref mirror of the effective identity so create-conversation calls read fresh state
  // without re-subscribing their useCallback deps.
  const effectiveIdentityRef = useRef<StudentIdentity | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.studentName || parsed?.studentNumber) {
          setIdentityRecord({ studentName: parsed.studentName || "", studentNumber: parsed.studentNumber || "" });
          setIdentityActive(!!parsed.active);
        }
      }
    } catch { /* storage unavailable — start anonymous */ }
  }, []);

  useEffect(() => {
    effectiveIdentityRef.current = identityActive && identityRecord ? identityRecord : null;
  }, [identityActive, identityRecord]);

  const persistIdentity = useCallback((record: StudentIdentity | null, active: boolean) => {
    try {
      if (record) localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify({ ...record, active }));
      else localStorage.removeItem(IDENTITY_STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  /** Apply a freshly entered identity: remember it, activate it, and stamp the live conversation. */
  const applyIdentity = useCallback(async (record: StudentIdentity) => {
    setIdentityRecord(record);
    setIdentityActive(true);
    clientRef.current.setIdentified(true);
    setShowIdentityGate(false);
    effectiveIdentityRef.current = record;
    persistIdentity(record, true);
    if (conversationId) {
      try { await clientRef.current.setIdentity(conversationId, record); }
      catch (err) { console.warn("[UWT Tutor Widget] Failed to save identity:", err); }
    }
  }, [conversationId, persistIdentity]);

  /** Re-activate a previously remembered identity (one click, no re-typing). */
  const reactivateIdentity = useCallback(async () => {
    // Back from anonymous while still signed in with UW: that is the UW identity
    // returning, not the typed one — there may be no typed name at all. Do what
    // sign-in does and let the token effect below open the new conversation. Never
    // stamp the open one: it is the anonymous guest conversation, and stays that way.
    if (clientRef.current.hasUserToken()) {
      setIdentityActive(true);
      setIdentityNote(conversationId
        ? "Signed in — this is a new chat. What you said anonymously stays anonymous."
        : null);
      return;
    }
    if (!identityRecord) { setShowIdentityGate(true); return; }
    setIdentityActive(true);
    clientRef.current.setIdentified(true);
    effectiveIdentityRef.current = identityRecord;
    persistIdentity(identityRecord, true);
    if (conversationId) {
      try { await clientRef.current.setIdentity(conversationId, identityRecord); }
      catch (err) { console.warn("[UWT Tutor Widget] Failed to save identity:", err); }
    }
  }, [conversationId, identityRecord, persistIdentity]);

  /**
   * Restored sessions: make the conversation row match what this browser believes.
   *
   * A conversation is stamped with identity at create time, but a student can identify
   * (or go anonymous) after it exists, on a different page or a later visit — the widget
   * would then show them as identified while the row stayed anonymous, silently costing
   * them credit. Reconcile in both directions, and only when the two actually differ.
   */
  const reconcileIdentity = useCallback(
    async (convId: string, stored: { studentName: string | null; studentNumber: string | null }) => {
      const wanted = effectiveIdentityRef.current;
      const wantedName = normalizeIdentityField(wanted?.studentName, MAX_STUDENT_NAME_LENGTH);
      const wantedNumber = normalizeIdentityField(wanted?.studentNumber, MAX_STUDENT_NUMBER_LENGTH);
      const storedName = normalizeIdentityField(stored.studentName, MAX_STUDENT_NAME_LENGTH);
      const storedNumber = normalizeIdentityField(stored.studentNumber, MAX_STUDENT_NUMBER_LENGTH);
      if (wantedName === storedName && wantedNumber === storedNumber) return;
      try {
        await clientRef.current.setIdentity(convId, wanted);
      } catch (err) {
        console.warn("[UWT Tutor Widget] Failed to sync identity on restore:", err);
      }
    },
    []
  );

  // ── UW NetID sign-in ────────────────────────────────────────────
  // Separate from the typed self-ID above. That one is a student asserting who they
  // are; this one is UW confirming it. Where both exist the verified one wins.
  // The handlers live below handleNewChat: switching between the guest token and the
  // user token has to start a new conversation, so they need it.
  const [signedInName, setSignedInName] = useState<string | null>(null);
  // Mirrors "a user token exists" as state. The effect below has to re-evaluate when
  // one arrives, and a student who signs in while already identified by a typed name
  // changes nothing else the effect watches.
  const [signedIn, setSignedIn] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [identityNote, setIdentityNote] = useState<string | null>(null);

  // Keyed widgets use the server-authoritative counter sourced from /widget/config
  // + SSE `done` events; keyless widgets fall back to the legacy localStorage counter
  // driven by the data-max-messages attribute.
  const hasKey = !!widgetKey;

  // ── Lazy conversation creation ──────────────────────────────
  // A conversation is created on the first message, not when the panel opens. In
  // TME 310, 43% of opens never sent anything, and each still made an empty
  // conversation that skewed the counts and used up the guest conversation cap.
  const conversationIdRef = useRef<string | null>(null);
  const creatingRef = useRef<Promise<string> | null>(null);
  const totalUserMessagesRef = useRef(0);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { totalUserMessagesRef.current = totalUserMessages; }, [totalUserMessages]);

  /** Save what survives a reload: the session always, the conversation once one exists. */
  const persistSession = useCallback(
    (cfg: WidgetConfig, convId: string | null) => {
      const client = clientRef.current;
      saveSession(sessionKey, {
        token: client.getToken()!,
        ...(convId ? { conversationId: convId } : {}),
        config: cfg,
        savedAt: Date.now(),
        totalUserMessages: totalUserMessagesRef.current,
        pageContextId: pageContextIdRef.current,
        pageUrl: hostPage().pageUrl,
        uwSignedIn: client.isIdentified(),
      });
    },
    [sessionKey]
  );

  /**
   * The open conversation's id, creating it on first use. Concurrent callers share one
   * create. Whichever token is active at this moment owns it, so a sign-in or "go
   * anonymous" before the first message needs nothing more than clearing the id.
   */
  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationIdRef.current) return conversationIdRef.current;
    if (creatingRef.current) return creatingRef.current;
    const cfg = config;
    if (!cfg) throw new Error("The tutor is still starting up. Please try again.");
    const client = clientRef.current;
    const creating = (async () => {
      // Always re-read the page for a new conversation. A capture saved with the session
      // may be from before the page was edited, or from another page entirely. An
      // unchanged page is a hash plus one cached lookup, not another distillation.
      if (cfg.autoPageContext) {
        pageContextIdRef.current = await resolvePageContext();
      }
      const conv = await client.createConversation({
        courseId: cfg.courseId,
        sectionId: cfg.sectionId,
        assignmentId: cfg.assignmentId,
        conversationTypeId: cfg.conversationTypeId,
        title: cfg.assignmentName ? `Widget: ${cfg.assignmentName}` : `Widget: ${cfg.courseCode}`,
        pageContextId: pageContextIdRef.current,
        studentName: effectiveIdentityRef.current?.studentName,
        studentNumber: effectiveIdentityRef.current?.studentNumber,
        ...hostPage(),
      });
      conversationIdRef.current = conv.id;
      setConversationId(conv.id);
      persistSession(cfg, conv.id);
      return conv.id;
    })();
    creatingRef.current = creating;
    try {
      return await creating;
    } catch (err) {
      // A create refused for the conversation cap arrives here, not as an SSE error,
      // so the session-fatal check on chat.errorCode would never see it.
      const code = (err as Error & { code?: string }).code;
      const message = (err as Error).message ?? "";
      if ((code && SESSION_FATAL_CODES.includes(code)) || message.includes("Maximum conversations")) {
        setSessionExhausted(true);
      }
      throw err;
    } finally {
      creatingRef.current = null;
    }
  }, [config, resolvePageContext, persistSession]);

  // useChat is always called (hooks rules); with no conversation yet, its first send
  // creates one through ensureConversation.
  const chat = useChat({
    client: clientRef.current,
    conversationId: conversationId || "",
    ensureConversation,
    initialMessages: restoredMessages,
    onUsage: hasKey ? setServerUsage : undefined,
  });

  // When the server returns a session-fatal error, lock the widget permanently
  useEffect(() => {
    if (chat.errorCode && SESSION_FATAL_CODES.includes(chat.errorCode)) {
      setSessionExhausted(true);
    }
  }, [chat.errorCode]);

  // Keyed mode: proactively lock the widget when the server-authoritative
  // counter shows no messages remaining, so we match the server-side gate
  // without waiting for the next send to bounce.
  useEffect(() => {
    if (hasKey && serverUsage && serverUsage.messagesRemaining <= 0) {
      setSessionExhausted(true);
    }
  }, [hasKey, serverUsage]);

  const handleOpen = useCallback(async () => {
    if (widgetState === "ready" || widgetState === "initializing") {
      setIsOpen(true);
      return;
    }

    setIsOpen(true);
    setWidgetState("initializing");
    setInitError(null);

    const client = clientRef.current;

    // ── Try to restore a saved session ──────────────────────
    // A conversation created under a UW sign-in is not restored: the sign-in does not
    // survive a reload (see SavedSession.uwSignedIn), and resuming an attributed
    // conversation in a widget that now reads "Anonymous" would mislead the student
    // about the one thing they were given a choice over. It falls through to the fresh
    // path below, which opens a new guest session and a new conversation.
    const saved = loadSession(sessionKey);
    if (saved?.uwSignedIn) {
      setIdentityNote("Signing in lasts for one visit — this is a new, anonymous chat.");
    }
    // A page-reading bot's conversation is about the page it was started on: opened on a
    // different page, keep the session but start fresh there. Sessions saved before
    // pageUrl was recorded are restored as they always were.
    const otherPage =
      !!saved?.config?.autoPageContext && !!saved.pageUrl && saved.pageUrl !== hostPage().pageUrl;
    const restoreConversation = !!saved?.conversationId && !otherPage;
    if (saved && !saved.uwSignedIn && !restoreConversation) {
      // Opened before, never sent a message: nothing to load. Re-fetching the config
      // checks the token is still good and refreshes the usage snapshot.
      try {
        client.restoreToken(saved.token);
        const fresh = await client.getWidgetConfig({ courseId, sectionId, assignmentId });
        setConfig(fresh);
        if (hasKey && fresh.usage) setServerUsage(fresh.usage);
        setRestoredMessages([]);
        setTotalUserMessages(saved.totalUserMessages || 0);
        // No conversation restored, so no capture to inherit: the first message reads
        // this page (ensureConversation).
        pageContextIdRef.current = undefined;
        setWidgetState("ready");
        return;
      } catch {
        clearSession(sessionKey);
      }
    } else if (saved && !saved.uwSignedIn && restoreConversation && saved.conversationId) {
      try {
        client.restoreToken(saved.token);
        // Verify the conversation still exists and load messages
        const conv = await client.getConversation(saved.conversationId);
        setConfig(saved.config);
        setConversationId(saved.conversationId);
        // Replies that came back empty (before the server turned those into a retryable
        // error) would restore as blank bubbles.
        setRestoredMessages(conv.messages.filter((m) => m.role !== "assistant" || m.content.trim() !== ""));
        setTotalUserMessages(saved.totalUserMessages || 0);
        pageContextIdRef.current = saved.pageContextId;
        // Identity may have changed since this conversation was created (other page,
        // later visit) — push the current local state onto the row if it drifted.
        await reconcileIdentity(saved.conversationId, {
          studentName: conv.studentName,
          studentNumber: conv.studentNumber,
        });
        // Keyed mode: refetch config to get a fresh server-authoritative usage snapshot.
        if (hasKey) {
          try {
            const fresh = await client.getWidgetConfig({ courseId, sectionId, assignmentId });
            setConfig(fresh);
            if (fresh.usage) setServerUsage(fresh.usage);
          } catch { /* fall through — restore still usable */ }
        }
        setWidgetState("ready");
        return;
      } catch {
        // Saved session is invalid (expired, deleted, etc.) — start fresh
        clearSession(sessionKey);
      }
    }

    // ── Fresh session ───────────────────────────────────────
    try {
      // 1. Init guest session (gets JWT)
      await client.initGuestSession(widgetKey);

      // 2. Fetch widget config (validates UUIDs, gets display names + conversation type)
      const widgetConfig = await client.getWidgetConfig({
        courseId,
        sectionId,
        assignmentId,
      });
      setConfig(widgetConfig);
      if (hasKey && widgetConfig.usage) {
        setServerUsage(widgetConfig.usage);
      }

      // Carry a staff budget alert out if one is waiting. Fire-and-forget and
      // silent: at most twice a month, and never the student's concern.
      if (widgetConfig.pendingAlert) {
        void client.relayBudgetAlert(widgetConfig.pendingAlert);
      }

      // 3. A section that requires attribution has nothing to open a conversation
      // with yet. Stop here rather than creating one the server would refuse
      // (403 SIGN_IN_REQUIRED) and showing the student an error where a sign-in
      // prompt belongs. The panel renders ready, with the input gated and the reason
      // stated; the first message after signing in creates the conversation.
      if (widgetConfig.allowAnonymousChat === false && !client.isIdentified()) {
        setRestoredMessages([]);
        setWidgetState("ready");
        return;
      }

      // 4. Self-reading widgets: resolve page context before the first conversation
      if (widgetConfig.autoPageContext) {
        pageContextIdRef.current = await resolvePageContext();
      }

      // 5. No conversation yet: the first message creates it (ensureConversation).
      setRestoredMessages([]);
      setWidgetState("ready");

      // 6. Save the session so a reload reuses it instead of starting another.
      persistSession(widgetConfig, null);
    } catch (err) {
      console.error("[CSS Tutor Widget] Initialization failed:", err);
      setInitError((err as Error).message);
      setWidgetState("error");
    }
  }, [widgetState, courseId, sectionId, assignmentId, widgetKey, hasKey, sessionKey, resolvePageContext, reconcileIdentity, persistSession]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Start over: clear the transcript and the conversation id. The next message creates
   * the new conversation, under whichever token is active then — so this is also how
   * a switch between the guest session and a UW sign-in moves to a new conversation.
   */
  const handleNewChat = useCallback(async () => {
    chat.resetMessages();
    setRestoredMessages([]);
    conversationIdRef.current = null;
    creatingRef.current = null;
    setConversationId(null);
    if (config) persistSession(config, null);
  }, [config, persistSession]);

  const handleSignIn = useCallback(async () => {
    setSignInError(null);
    setIdentityNote(null);
    setSigningIn(true);
    try {
      // Not awaited before the popup opens — see signInWithUwNetId.
      const result = await clientRef.current.signInWithUwNetId();
      if (result) {
        setSignedInName(result.displayName || "your UW account");
        setSignedIn(true);
        // Signing in is itself the choice to be identified.
        setIdentityActive(true);
        // The conversation switch itself is handled by the effect below, which
        // watches the token rather than the button that changed it.
        if (conversationId) {
          setIdentityNote("Signed in — this is a new chat. What you said anonymously stays anonymous.");
        }
      } else {
        setSignInError("Sign-in did not complete. If nothing opened, allow popups for this site.");
      }
    } catch {
      setSignInError("Sign-in failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  }, [conversationId]);

  /** Stop attributing what the student says from here on ("go anonymous"). */
  const goAnonymous = useCallback(async () => {
    // Whether the live conversation is owned by their UW account, which decides
    // whether this is a metadata change or a change of conversation.
    const wasSignedIn = clientRef.current.isIdentified();

    setIdentityActive(false);
    setSignInError(null);
    // Switches new conversations back onto the guest token, which is what makes
    // this anonymity rather than a hidden attribution.
    clientRef.current.setIdentified(false);
    effectiveIdentityRef.current = null;
    if (identityRecord) persistIdentity(identityRecord, false);

    if (wasSignedIn) {
      // The open conversation is owned by their UW account. Clearing the name off it
      // would leave that ownership in place — an attributed row wearing an anonymous
      // label, the one thing this feature promises never to produce. So it is left as
      // theirs and the effect below moves them to a fresh guest conversation.
      setIdentityNote("Now anonymous — this is a new chat. The earlier one stays under your UW account.");
      return;
    }

    setIdentityNote(null);
    if (conversationId) {
      try { await clientRef.current.setIdentity(conversationId, null); }
      catch (err) { console.warn("[UWT Tutor Widget] Failed to clear identity:", err); }
    }
  }, [conversationId, identityRecord, persistIdentity]);

  /**
   * Keep the conversation and the token that owns it in step.
   *
   * A conversation belongs to whichever session created it — the guest session or the
   * student's UW account — and the server enforces that. So a change of token is a
   * change of conversation, not something that can be applied to the open one. This
   * watches the token rather than the four buttons that can change it (sign in, go
   * anonymous, use my name, enter a name), because missing one of them would strand a
   * student on a conversation the server will refuse their next message on.
   *
   * No user token means no token switching: with only the guest session in play this
   * never fires, and the typed self-ID keeps behaving exactly as it did before.
   */
  const usingUserTokenRef = useRef(false);
  useEffect(() => {
    const client = clientRef.current;
    const identified = identityActive && client.hasUserToken();
    client.setIdentified(identified);
    if (identified === usingUserTokenRef.current) return;
    usingUserTokenRef.current = identified;
    // Also covers the section that requires sign-in, where init deliberately left the
    // widget with no conversation at all: signing in is what creates the first one.
    if (config) void handleNewChat();
  }, [identityActive, signedIn, config, handleNewChat]);

  const handleChatModelChange = useCallback((key: string) => {
    setChatModel(key);
    clientRef.current.setChatModel(key);
  }, []);

  const handleSend = useCallback(
    (content: string) => {
      // The note explains why the transcript changed; once they are typing into the
      // new one it has done its job.
      setIdentityNote(null);
      chat.sendMessage(content);
      // Keyless (legacy) mode: maintain the localStorage-backed counter.
      // Keyed mode: the counter comes from the server via SSE `done` — skip.
      if (!hasKey) {
        setTotalUserMessages((prev) => {
          const newTotal = prev + 1;
          try {
            const raw = localStorage.getItem(sessionKey);
            if (raw) {
              const session = JSON.parse(raw);
              session.totalUserMessages = newTotal;
              localStorage.setItem(sessionKey, JSON.stringify(session));
            }
          } catch { /* ignore */ }
          return newTotal;
        });
      }
    },
    [conversationId, chat.sendMessage, sessionKey, hasKey]
  );

  const positionClass =
    position === "bottom-left" ? " widget-fab--left" : "";

  return (
    <>
      {/* FAB button — always visible when panel is closed */}
      {!isOpen && (
        <button
          class={`widget-fab${positionClass}`}
          onClick={handleOpen}
          title="Open UWT Tutor AI chat"
        >
          {logoUrl ? <img class="widget-fab-logo" src={logoUrl} alt="Chat" /> : <ChatIcon />}
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <>
          {widgetState === "initializing" && (
            <div
              class={`widget-panel${position === "bottom-left" ? " widget-panel--left" : ""}`}
            >
              <div class="widget-header">
                <div class="widget-header-info">
                  <div class="widget-header-title">UWT Tutor AI</div>
                  <div class="widget-header-subtitle">Connecting...</div>
                </div>
                <button
                  class="widget-close-btn"
                  onClick={handleClose}
                  title="Close chat"
                >
                  <svg
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
              <div class="widget-loading">Starting tutor session...</div>
            </div>
          )}

          {widgetState === "error" && (
            <div
              class={`widget-panel${position === "bottom-left" ? " widget-panel--left" : ""}`}
            >
              <div class="widget-header">
                <div class="widget-header-info">
                  <div class="widget-header-title">UWT Tutor AI</div>
                </div>
                <button
                  class="widget-close-btn"
                  onClick={handleClose}
                  title="Close chat"
                >
                  <svg
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
              <div class="widget-error">
                <span>{initError || "Failed to connect"}</span>
              </div>
              <div class="widget-welcome">
                <div class="widget-welcome-text">
                  Unable to start tutor session. Please try again later.
                </div>
              </div>
              <div class="widget-input-area">
                <button
                  class="widget-send-btn"
                  style={{ width: "auto", borderRadius: "8px", padding: "8px 16px" }}
                  onClick={() => {
                    setWidgetState("idle");
                    handleOpen();
                  }}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {widgetState === "ready" && config && (
            <ChatPanel
              config={config}
              messages={chat.messages}
              isStreaming={chat.isStreaming}
              error={chat.error}
              retryable={chat.retryable}
              onSend={handleSend}
              onClose={handleClose}
              onClearError={chat.clearError}
              onRetry={chat.retry}
              onNewChat={handleNewChat}
              position={position}
              // Keyed mode sources counter from server; keyless from data-max-messages.
              maxMessages={hasKey ? serverUsage?.messageLimit : maxMessages}
              totalUserMessages={hasKey ? (serverUsage?.messagesUsed ?? 0) : totalUserMessages}
              sessionExhausted={sessionExhausted}
              showInfo={showInfo}
              onToggleInfo={() => setShowInfo(!showInfo)}
              identityRecord={identityRecord}
              identityActive={identityActive}
              showIdentityGate={showIdentityGate}
              onApplyIdentity={applyIdentity}
              onReactivateIdentity={reactivateIdentity}
              onGoAnonymous={goAnonymous}
              onOpenIdentityGate={() => setShowIdentityGate(true)}
              onCloseIdentityGate={() => setShowIdentityGate(false)}
              signedInName={signedInName}
              signingIn={signingIn}
              signInError={signInError}
              identityNote={identityNote}
              onSignIn={handleSignIn}
              allowAnonymousChat={config.allowAnonymousChat !== false}
              chatModel={chatModel}
              onChatModelChange={handleChatModelChange}
            />
          )}
        </>
      )}
    </>
  );
}
